import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    Output,
    SimpleChanges,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import {
    ClickmeButtonDirective,
    ClickmeLoaderDirective,
    ClickmeOtpInputDirective,
} from '@clickme/angular';

@Component({
    selector: 'rose-portal-otp-entry-view',
    standalone: true,
    imports: [
        ClickmeButtonDirective,
        ClickmeLoaderDirective,
        ClickmeOtpInputDirective,
    ],
    templateUrl: './otp-entry-view.component.html',
    styleUrl: './otp-entry-view.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
})
export class RoseOtpEntryViewComponent implements OnChanges, AfterViewInit, OnDestroy {
    @Input({ required: true }) destinationMasked!: string;
    @Input({ required: true }) channel!: 'sms' | 'email';
    @Input() length: number = 4;
    @Input({ required: true }) headingLabel!: string;
    @Input({ required: true }) goBackLabel!: string;
    @Input() hasError: boolean = false;
    @Input() errorLabel: string = '';

    @ViewChild('otpInput', { read: ElementRef }) otpInputRef!: ElementRef<HTMLElement>;

    @Output() codeEntered = new EventEmitter<string>();
    @Output() goBack = new EventEmitter<void>();

    #lastEmittedCode = '';

    #blockNonDigits = (e: KeyboardEvent): void => {
        if (e.key.length === 1 && !/\d/.test(e.key) && !e.ctrlKey && !e.metaKey) {
            e.preventDefault();
        }
    };

    ngAfterViewInit(): void {
        this.otpInputRef?.nativeElement.addEventListener('keydown', this.#blockNonDigits, true);
    }

    ngOnDestroy(): void {
        this.otpInputRef?.nativeElement.removeEventListener('keydown', this.#blockNonDigits, true);
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['hasError']?.currentValue) {
            this.#lastEmittedCode = '';
        }
    }

    /**
     * SNP-140: on @clickme/angular 0.22 the directive declares no outputs, so this binding resolves
     * to the native DOM listener only and runs once per event. Measured in
     * `otp-entry-view.component.double-fire.spec.ts` rather than inferred — on 0.19.1 the directive
     * aliased a `change` output and every code ran through here twice.
     *
     * <p>`#lastEmittedCode` is therefore a genuine retry de-dupe only: it stops one code being
     * submitted twice while the first attempt is still settling. It is cleared whenever the parent
     * reports a rejection ({@link ngOnChanges}), so resubmitting the SAME digits after a failure is
     * never swallowed.
     *
     * <p>The payload guard matches the login screen: shadow-DOM fields emit their own composed
     * `input`/`change` events which also reach this host, and only the component's own CustomEvents
     * carry a string value. Reading `detail` itself as a fallback would let one of those through as
     * an object and compare it against the guard as `[object Object]`.
     */
    onOtpChange(event: Event): void {
        const detail = (event as CustomEvent<{ value?: string }>).detail;
        if (typeof detail?.value !== 'string') {
            return;
        }
        const otpCode = detail.value;
        if (otpCode.length === this.length && otpCode !== this.#lastEmittedCode) {
            this.#lastEmittedCode = otpCode;
            this.codeEntered.emit(otpCode);
        }
    }

    onGoBack(): void {
        this.goBack.emit();
    }
}
